import { AxiosError } from 'axios'
import {
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  Eye,
  History,
  Landmark,
  Printer,
  RefreshCw,
  ShieldAlert,
  WalletCards,
  X,
} from 'lucide-react'
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import { CashSessionDetailModal } from '../components/pos/CashSessionDetailModal'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Button } from '../components/ui/button'
import { GlobalPaginator } from '../components/ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { TableSkeleton } from '../components/ui/table-skeleton'
import {
  cashHistoryService,
  type CashDifferenceFilter,
  type CashHistoryBranch,
  type CashHistoryRegister,
  type CashHistoryView,
  type CashMovementDirection,
  type CashMovementFilters,
  type CashMovementHistory,
  type CashMovementSummary,
  type CashMovementType,
  type CashSessionFilters,
  type CashSessionHistory,
  type CashSessionStatus,
  type CashSessionSummary,
} from '../services/cash-history.service'
import { cashRegisterService } from '../services/cash-register.service'
import { layoutService } from '../services/layout.service'

const PER_PAGE = 10

const sessionStatusConfig: Record<CashSessionStatus, { label: string; className: string }> = {
  OPEN: { label: 'Abierta', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  CLOSED: { label: 'Cerrada', className: 'border-slate-200 bg-slate-100 text-slate-700' },
  CANCELLED: { label: 'Cancelada', className: 'border-red-200 bg-red-50 text-red-700' },
}

const movementTypeConfig: Record<CashMovementType, { label: string; className: string }> = {
  OPENING: { label: 'Apertura', className: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
  SALE: { label: 'Venta', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  RETURN: { label: 'Devolución', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  WITHDRAWAL: { label: 'Retiro', className: 'border-red-200 bg-red-50 text-red-700' },
  DEPOSIT: { label: 'Depósito', className: 'border-blue-200 bg-blue-50 text-blue-700' },
  CLOSING_ADJUSTMENT: { label: 'Ajuste de cierre', className: 'border-violet-200 bg-violet-50 text-violet-700' },
}

interface PaginationState {
  currentPage: number
  total: number
  perPage: number
  from: number
  to: number
}

const emptyPagination: PaginationState = {
  currentPage: 1,
  total: 0,
  perPage: PER_PAGE,
  from: 0,
  to: 0,
}

function todayISO(): string {
  return new Date().toLocaleDateString('en-CA')
}

function yesterdayISO(): string {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  return date.toLocaleDateString('en-CA')
}

function formatMoney(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '—'

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function fullName(user: { name: string; last_name?: string | null } | null): string {
  if (!user) return '—'
  return [user.name, user.last_name].filter(Boolean).join(' ')
}

function referenceLabel(movement: CashMovementHistory): string | null {
  return movement.sale_return?.return_number ?? movement.sale?.ticket_number ?? null
}

function SummaryCards({
  items,
}: {
  items: Array<{
    label: string
    value: string
    tone?: string
    Icon: ComponentType<{ className?: string }>
    iconClassName: string
    iconContainerClassName: string
  }>
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
      {items.map(({ Icon, ...item }) => (
        <div
  key={item.label}
        className="rounded-lg border border-border bg-table-bg p-6 text-table-foreground shadow-sm"
          >
          <div className="flex min-w-0 items-center gap-3">
            <div className={`rounded-lg p-2 ${item.iconContainerClassName}`}>
              <Icon className={`h-5 w-5 ${item.iconClassName}`} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-table-foreground/70">
  {item.label}
</p>

<p className={`truncate text-2xl font-semibold ${item.tone ?? 'text-table-foreground'}`}>
  {item.value}
</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function StatusBadge({ status }: { status: CashSessionStatus }) {
  const config = sessionStatusConfig[status]
  return <span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${config.className}`}>{config.label}</span>
}

function MovementBadge({ type }: { type: CashMovementType }) {
  const config = movementTypeConfig[type]
  return <span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${config.className}`}>{config.label}</span>
}

export function CashHistory() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedView = searchParams.get('view')
  const [view, setView] = useState<CashHistoryView>(requestedView === 'movements' ? 'movements' : 'sessions')
  const [branches, setBranches] = useState<CashHistoryBranch[]>([])
  const [registers, setRegisters] = useState<CashHistoryRegister[]>([])
  const [selectedBranch, setSelectedBranch] = useState('all')
  const [selectedRegister, setSelectedRegister] = useState('all')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [sessionStatus, setSessionStatus] = useState<'all' | CashSessionStatus>('all')
  const [difference, setDifference] = useState<'all' | CashDifferenceFilter>('all')
  const [movementType, setMovementType] = useState<'all' | CashMovementType>('all')
  const [direction, setDirection] = useState<'all' | CashMovementDirection>('all')
  const [page, setPage] = useState(1)
  const [sessions, setSessions] = useState<CashSessionHistory[]>([])
  const [movements, setMovements] = useState<CashMovementHistory[]>([])
  const [sessionSummary, setSessionSummary] = useState<CashSessionSummary | null>(null)
  const [movementSummary, setMovementSummary] = useState<CashMovementSummary | null>(null)
  const [pagination, setPagination] = useState<PaginationState>(emptyPagination)
  const [isLoading, setIsLoading] = useState(true)
  const [isForbidden, setIsForbidden] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [detailSessionId, setDetailSessionId] = useState<number | null>(null)
  const [autoPrintSessionId, setAutoPrintSessionId] = useState<number | null>(null)

  useEffect(() => {
    layoutService.getMySucursales()
      .then((items) => setBranches(items))
      .catch(() => setBranches([]))
  }, [])

  useEffect(() => {
    if (selectedBranch === 'all') {
      setRegisters([])
      setSelectedRegister('all')
      return
    }

    cashRegisterService.getItems({
      all: true,
      sucursal_id: Number(selectedBranch),
    }).then((response) => {
      const payload = response?.data
      const items = Array.isArray(payload) ? payload : payload?.data ?? []
      setRegisters(items)
    }).catch(() => setRegisters([]))
  }, [selectedBranch])

  const commonFilters = useMemo(() => ({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    sucursal_id: selectedBranch !== 'all' ? Number(selectedBranch) : undefined,
    cash_register_id: selectedRegister !== 'all' ? Number(selectedRegister) : undefined,
    from: from || undefined,
    to: to || undefined,
  }), [from, page, search, selectedBranch, selectedRegister, to])

  const fetchHistory = useCallback(async () => {
    setIsLoading(true)
    setIsForbidden(false)
    setHasError(false)

    try {
      if (view === 'sessions') {
        const filters: CashSessionFilters = {
          ...commonFilters,
          status: sessionStatus !== 'all' ? sessionStatus : undefined,
          difference: difference !== 'all' ? difference : undefined,
        }
        const result = await cashHistoryService.getSessions(filters)
        setSessions(result.sessions.data)
        setSessionSummary(result.summary)
        setPagination({
          currentPage: result.sessions.current_page,
          total: result.sessions.total,
          perPage: result.sessions.per_page,
          from: result.sessions.from ?? 0,
          to: result.sessions.to ?? 0,
        })
      } else {
        const filters: CashMovementFilters = {
          ...commonFilters,
          movement_type: movementType !== 'all' ? movementType : undefined,
          direction: direction !== 'all' ? direction : undefined,
        }
        const result = await cashHistoryService.getMovements(filters)
        setMovements(result.movements.data)
        setMovementSummary(result.summary)
        setPagination({
          currentPage: result.movements.current_page,
          total: result.movements.total,
          perPage: result.movements.per_page,
          from: result.movements.from ?? 0,
          to: result.movements.to ?? 0,
        })
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 403) {
        setIsForbidden(true)
      } else {
        setHasError(true)
      }
      setPagination(emptyPagination)
    } finally {
      setIsLoading(false)
    }
  }, [commonFilters, difference, direction, movementType, sessionStatus, view])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const changeView = (nextView: CashHistoryView) => {
    setView(nextView)
    setSearchParams({ view: nextView })
    setSessionStatus('all')
    setDifference('all')
    setMovementType('all')
    setDirection('all')
    setPage(1)
    setDetailSessionId(null)
    setAutoPrintSessionId(null)
  }

  const setQuickDate = (kind: 'today' | 'yesterday') => {
    const date = kind === 'today' ? todayISO() : yesterdayISO()
    setFrom(date)
    setTo(date)
    setPage(1)
  }

  const resetFilters = () => {
    setSelectedBranch('all')
    setSelectedRegister('all')
    setSearch('')
    setFrom('')
    setTo('')
    setSessionStatus('all')
    setDifference('all')
    setMovementType('all')
    setDirection('all')
    setPage(1)
  }

  const hasFilters = selectedBranch !== 'all'
    || selectedRegister !== 'all'
    || search !== ''
    || from !== ''
    || to !== ''
    || sessionStatus !== 'all'
    || difference !== 'all'
    || movementType !== 'all'
    || direction !== 'all'

  const sessionSummaryItems = [
    {
      label: 'Sesiones del mes',
      value: String(sessionSummary?.sessions_count ?? 0),
      Icon: History,
      iconContainerClassName: 'bg-primary/10',
      iconClassName: 'text-primary',
    },
    {
      label: 'Abiertas del mes',
      value: String(sessionSummary?.status.open ?? 0),
      tone: 'text-white-700',
      Icon: Landmark,
      iconContainerClassName: 'bg-emerald-500/10',
      iconClassName: 'text-emerald-400',
    },
    {
      label: 'Cerradas del mes',
      value: String(sessionSummary?.status.closed ?? 0),
      Icon: WalletCards,
      iconContainerClassName: 'bg-slate-100',
      iconClassName: 'text-slate-600',
    },
    {
      label: 'Diferencia del mes',
      value: formatMoney(sessionSummary?.difference_total),
      tone: Number(sessionSummary?.difference_total ?? 0) < 0
  ? 'text-red-400'
  : 'text-table-foreground',
      Icon: ArrowUpRight,
      iconContainerClassName: 'bg-yellow-50',
      iconClassName: 'text-yellow-600',
    },
  ]
  const movementSummaryItems = [
    {
      label: 'Movimientos del mes',
      value: String(movementSummary?.movements_count ?? 0),
      Icon: WalletCards,
      iconContainerClassName: 'bg-primary/10',
      iconClassName: 'text-primary',
    },
    {
      label: 'Entradas del mes',
      value: formatMoney(movementSummary?.incoming_total),
      tone: 'text-white-700',
      Icon: ArrowDownLeft,
      iconContainerClassName: 'bg-green-50',
      iconClassName: 'text-green-600',
    },
    {
      label: 'Salidas del mes',
      value: formatMoney(movementSummary?.outgoing_total),
      tone: 'text-white-700',
      Icon: ArrowUpRight,
      iconContainerClassName: 'bg-red-50',
      iconClassName: 'text-red-600',
    },
    {
      label: 'Saldo neto del mes',
      value: formatMoney(movementSummary?.net_total),
      tone: Number(movementSummary?.net_total ?? 0) < 0 ? 'text-white-700' : 'text-slate-100',
      Icon: Landmark,
      iconContainerClassName: 'bg-purple-50',
      iconClassName: 'text-purple-600',
    },
  ]

  return (
    <div className="w-full min-w-0 space-y-5 p-4 sm:p-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Historial de caja</h1>
          <p className="mt-1 text-sm text-slate-500">Consulta cortes, responsables y movimientos de efectivo.</p>
        </div>
        <div className="w-full min-w-0 sm:w-72">
          <label className="mb-1.5 block text-xs font-medium uppercase text-card-description/400">Vista</label>
          <Select value={view} onValueChange={(value) => changeView(value as CashHistoryView)}>
            <SelectTrigger aria-label="Vista del historial" className="h-10 w-full min-w-0 border-border bg-card text-card-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sessions"><Landmark /> Sesiones de caja</SelectItem>
              <SelectItem value="movements"><WalletCards /> Movimientos de efectivo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

     {!isForbidden && !hasError && (
  <SummaryCards items={view === 'sessions' ? sessionSummaryItems : movementSummaryItems} />
)}

      
         <GlobalFilterBar
  show={['search']}
  values={{
    search,
  }}
  onChange={(changes) => {
    if (changes.search !== undefined) {
      setSearch(changes.search)
      setPage(1)
    }
  }}
  customOptions={{
    searchPlaceholder:
      view === 'sessions'
        ? 'Buscar sesión, caja o responsable...'
        : 'Buscar concepto, ticket o devolución...',
  }}
>
  <Select
    value={selectedBranch}
    onValueChange={(value) => {
      setSelectedBranch(value)
      setSelectedRegister('all')
      setPage(1)
    }}
  >
    <SelectTrigger aria-label="Sucursal" className="h-10 min-w-[190px]">
      <SelectValue placeholder="Todas las sucursales" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="all">Todas las sucursales</SelectItem>

      {branches.map((branch) => (
        <SelectItem key={branch.id} value={String(branch.id)}>
          {branch.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>

  <Select
    value={selectedRegister}
    disabled={selectedBranch === 'all'}
    onValueChange={(value) => {
      setSelectedRegister(value)
      setPage(1)
    }}
  >
    <SelectTrigger aria-label="Caja" className="h-10 min-w-[180px]">
      <SelectValue placeholder="Todas las cajas" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="all">Todas las cajas</SelectItem>

      {registers.map((register) => (
        <SelectItem key={register.id} value={String(register.id)}>
          {register.name} · {register.code}
          {!register.is_active ? ' (inactiva)' : ''}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>

  {view === 'sessions' ? (
    <>
      <Select
        value={sessionStatus}
        onValueChange={(value) => {
          setSessionStatus(value as typeof sessionStatus)
          setPage(1)
        }}
      >
        <SelectTrigger
          aria-label="Estado de sesión"
          className="h-10 min-w-[170px]"
        >
          <SelectValue />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Todos los estados</SelectItem>
          <SelectItem value="OPEN">Abiertas</SelectItem>
          <SelectItem value="CLOSED">Cerradas</SelectItem>
          <SelectItem value="CANCELLED">Canceladas</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={difference}
        onValueChange={(value) => {
          setDifference(value as typeof difference)
          setPage(1)
        }}
      >
        <SelectTrigger
          aria-label="Diferencia de caja"
          className="h-10 min-w-[185px]"
        >
          <SelectValue />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Todas las diferencias</SelectItem>
          <SelectItem value="shortage">Con faltante</SelectItem>
          <SelectItem value="surplus">Con sobrante</SelectItem>
          <SelectItem value="exact">Corte exacto</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : (
    <>
      <Select
        value={movementType}
        onValueChange={(value) => {
          setMovementType(value as typeof movementType)
          setPage(1)
        }}
      >
        <SelectTrigger
          aria-label="Tipo de movimiento"
          className="h-10 min-w-[185px]"
        >
          <SelectValue />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Todos los tipos</SelectItem>

          {Object.entries(movementTypeConfig).map(([type, config]) => (
            <SelectItem key={type} value={type}>
              {config.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={direction}
        onValueChange={(value) => {
          setDirection(value as typeof direction)
          setPage(1)
        }}
      >
        <SelectTrigger aria-label="Dirección" className="h-10 min-w-[170px]">
          <SelectValue />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Entradas y salidas</SelectItem>
          <SelectItem value="IN">Entradas</SelectItem>
          <SelectItem value="OUT">Salidas</SelectItem>
        </SelectContent>
      </Select>
    </>
  )}

  <div className="flex min-w-[230px] items-center gap-2">
    <Calendar className="h-4 w-4 shrink-0 text-card-description" />

    <label
      htmlFor="cash-history-from"
      className="text-sm text-card-description"
    >
      Desde:
    </label>

    <input
      id="cash-history-from"
      type="date"
      value={from}
      max={to || todayISO()}
      onChange={(event) => {
        setFrom(event.target.value)
        setPage(1)
      }}
      className="h-10 min-w-0 flex-1 rounded-md border border-border bg-card px-2 text-sm text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
    />
  </div>

  <div className="flex min-w-[215px] items-center gap-2">
    <label
      htmlFor="cash-history-to"
      className="text-sm text-card-description"
    >
      Hasta:
    </label>

    <input
      id="cash-history-to"
      type="date"
      value={to}
      min={from}
      max={todayISO()}
      onChange={(event) => {
        setTo(event.target.value)
        setPage(1)
      }}
      className="h-10 min-w-0 flex-1 rounded-md border border-border bg-card px-2 text-sm text-card-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
    />
  </div>

  {(from || to) && (
    <button
      type="button"
      onClick={() => {
        setFrom('')
        setTo('')
        setPage(1)
      }}
      className="rounded-full border border-border bg-card px-3 py-1 text-xs text-card-foreground hover:bg-table-row-hover"
    >
      <X className="mr-1 inline h-3 w-3" />
      Limpiar fechas
    </button>
  )}
  

  {hasFilters && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={resetFilters}
    >
      <X className="h-4 w-4" />
      Limpiar filtros
    </Button>
  )}
  <div className="flex items-center gap-2">
  <button
    type="button"
    onClick={() => setQuickDate('today')}
    className={`rounded-full border px-3 py-1 text-xs font-medium ${
      from === todayISO() && to === todayISO()
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-card-foreground hover:bg-table-row-hover'
    }`}
  >
    Hoy
  </button>

  <button
    type="button"
    onClick={() => setQuickDate('yesterday')}
    className={`rounded-full border px-3 py-1 text-xs font-medium ${
      from === yesterdayISO() && to === yesterdayISO()
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-card-foreground hover:bg-table-row-hover'
    }`}
  >
    Ayer
  </button>
</div>
</GlobalFilterBar>
          
    

      {isForbidden ? (
        <div className="flex min-h-64 flex-col items-center justify-center border border-slate-200 bg-white px-6 text-center">
          <ShieldAlert className="h-10 w-10 text-amber-600" />
          <h2 className="mt-3 text-lg font-semibold text-slate-900">Acceso restringido</h2>
          <p className="mt-1 max-w-md text-sm text-slate-500">Tu rol no tiene permiso para consultar el historial de caja.</p>
        </div>
      ) : hasError ? (
        <div className="flex min-h-64 flex-col items-center justify-center border border-slate-200 bg-white px-6 text-center">
          <RefreshCw className="h-9 w-9 text-slate-400" />
          <h2 className="mt-3 text-lg font-semibold text-slate-900">No fue posible cargar el historial</h2>
          <Button className="mt-4" variant="outline" onClick={fetchHistory}><RefreshCw />Reintentar</Button>
        </div>
      ) : isLoading ? (
        <TableSkeleton
          headers={view === 'sessions'
            ? ['Apertura', 'Sucursal / Caja', 'Estado', 'Responsable', 'Fondo', 'Ventas', 'Corte', 'Diferencia', 'Acciones']
            : ['Fecha', 'Sucursal / Caja', 'Sesión', 'Tipo', 'Concepto', 'Usuario', 'Importe']}
          columns={view === 'sessions'
            ? ['text', 'text-two-lines', 'badge', 'text', 'text', 'text', 'text', 'text', 'actions']
            : ['text', 'text-two-lines', 'text', 'badge', 'text-two-lines', 'text', 'text']}
          rows={8}
        />
      ) : view === 'sessions' ? (
        <SessionsTable
          sessions={sessions}
          pagination={pagination}
          onPageChange={setPage}
          onOpenDetail={(id, autoPrint = false) => {
            setDetailSessionId(id)
            setAutoPrintSessionId(autoPrint ? id : null)
          }}
        />
      ) : (
        <MovementsTable movements={movements} pagination={pagination} onPageChange={setPage} />
      )}

      <CashSessionDetailModal
        sessionId={detailSessionId}
        open={detailSessionId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailSessionId(null)
            setAutoPrintSessionId(null)
          }
        }}
        autoPrint={autoPrintSessionId === detailSessionId && detailSessionId !== null}
        onPrintComplete={() => setAutoPrintSessionId(null)}
      />
    </div>
  )
}

function SessionsTable({
  sessions,
  pagination,
  onPageChange,
  onOpenDetail,
}: {
  sessions: CashSessionHistory[]
  pagination: PaginationState
  onPageChange: (page: number) => void
  onOpenDetail: (id: number, autoPrint?: boolean) => void
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1220px] whitespace-nowrap">
          <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
            <tr>{['Apertura', 'Sucursal / Caja', 'Estado', 'Responsable', 'Fondo inicial', 'Ventas', 'Esperado / Contado', 'Diferencia', 'Cierre', ''].map((header) => <th key={header} className="px-4 py-3 text-left text-xs font-medium uppercase text-table-header-foreground">{header}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sessions.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-14 text-center"><History className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-2 text-sm text-table-foreground/70">No hay sesiones con estos filtros.</p></td></tr>
            ) : sessions.map((session) => {
              const differenceValue = Number(session.difference_amount ?? 0)
              return (
                <tr key={session.id} className="transition-colors hover:bg-table-row-hover">
                  <td className="px-4 py-3 text-sm text-table-foreground">{formatDateTime(session.opened_at)}</td>
                  <td className="px-4 py-3"><p className="text-sm font-medium text-table-foreground">{session.sucursal?.name ?? '—'}</p><p className="text-xs text-table-foreground/70">{session.cash_register?.name ?? '—'} · {session.cash_register?.code ?? '—'}</p></td>
                  <td className="px-4 py-3"><StatusBadge status={session.status} /></td>
                  <td className="px-4 py-3 text-sm text-table-foreground">{fullName(session.opened_by)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-table-foreground">{formatMoney(session.opening_cash_amount)}</td>
                  <td className="px-4 py-3 text-xs text-table-foreground"><p>Efectivo: {formatMoney(session.cash_sales_amount)}</p><p>Débito: {formatMoney(session.card_sales_amount)}</p><p>Crédito: {formatMoney(session.credit_card_sales_amount)}</p><p>Transferencia: {formatMoney(session.transfer_sales_amount)}</p></td>
                  <td className="px-4 py-3 text-xs text-table-foreground"><p>Esperado: {formatMoney(session.expected_cash_amount)}</p><p>Contado: {formatMoney(session.counted_cash_amount)}</p></td>
                  <td className={`px-4 py-3 text-sm font-semibold ${differenceValue < 0 ? 'text-red-700' : differenceValue > 0 ? 'text-amber-700' : 'text-slate-700'}`}>{formatMoney(session.difference_amount)}</td>
                  <td className="px-4 py-3 text-sm text-table-foreground">{formatDateTime(session.closed_at ?? session.cancelled_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" title="Ver detalle" onClick={() => onOpenDetail(session.id)}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Imprimir ticket" onClick={() => onOpenDetail(session.id, true)}><Printer className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <GlobalPaginator totalItems={pagination.total} itemsPerPage={pagination.perPage} currentPage={pagination.currentPage} from={pagination.from} to={pagination.to} onPageChange={onPageChange} />
    </div>
  )
}

function MovementsTable({
  movements,
  pagination,
  onPageChange,
}: {
  movements: CashMovementHistory[]
  pagination: PaginationState
  onPageChange: (page: number) => void
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] whitespace-nowrap">
          <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
            <tr>
              {[
                'Fecha',
                'Sucursal / Caja',
                'Sesión',
                'Tipo',
                'Concepto / Referencia',
                'Usuario',
                'Importe',
              ].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-xs font-medium uppercase text-table-header-foreground"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
            {movements.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-14 text-center"><WalletCards className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-2 text-sm text-slate-500">No hay movimientos con estos filtros.</p></td></tr>
            ) : movements.map((movement) => {
              const isIncoming = movement.direction === 'IN'
              const reference = referenceLabel(movement)
              return (
                <tr
                  key={movement.id}
                  className="transition-colors hover:bg-table-row-hover"
                >
                  <td className="px-4 py-3 text-sm text-table-foreground">{formatDateTime(movement.occurred_at)}</td>
                  <td className="px-4 py-3"><p className="text-sm font-medium text-table-foreground">{movement.sucursal?.name ?? '—'}</p><p className="text-xs text-table-foreground/70">{movement.cash_register?.name ?? '—'} · {movement.cash_register?.code ?? '—'}</p></td>
                  <td className="px-4 py-3 text-sm text-table-foreground">#{movement.cash_register_session_id}</td>
                  <td className="px-4 py-3"><MovementBadge type={movement.movement_type} /><p className={`mt-1 flex items-center gap-1 text-xs ${isIncoming ? 'text-emerald-700' : 'text-red-700'}`}>{isIncoming ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}{isIncoming ? 'Entrada' : 'Salida'}</p></td>
                  <td className="max-w-80 px-4 py-3"><p className="truncate text-sm text-table-foreground" title={movement.concept}>{movement.concept}</p>{reference && <p className="mt-0.5 font-mono text-xs text-table-foreground/70">{reference}</p>}</td>
                  <td className="px-4 py-3 text-sm text-table-foreground">{fullName(movement.user)}</td>
                  <td className={`px-4 py-3 text-sm font-semibold ${isIncoming ? 'text-emerald-700' : 'text-red-700'}`}>{isIncoming ? '+' : '−'} {formatMoney(movement.amount)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <GlobalPaginator totalItems={pagination.total} itemsPerPage={pagination.perPage} currentPage={pagination.currentPage} from={pagination.from} to={pagination.to} onPageChange={onPageChange} />
    </div>
  )
}
